import { Button } from '@heidi/design-system/button';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
} from '@heidi/design-system/popover';
import { typographyVariant } from '@heidi/design-system/typography';
import { Loader } from '@heidi/snowflake-ui/loader';
import { Info, X } from 'lucide-react';
import { useIntl } from 'react-intl';

type TauriUpdaterPromptProps = {
  open: boolean;
  isInstalling: boolean;
  onInstall: () => void;
  onClose: () => void;
};

export function TauriUpdaterPrompt({
  open,
  isInstalling,
  onInstall,
  onClose,
}: TauriUpdaterPromptProps) {
  const { formatMessage } = useIntl();

  return (
    <Popover open={open}>
      <PopoverAnchor className="fixed bottom-4 start-4" />
      <PopoverContent
        className="!pointer-events-auto relative z-999 w-[21rem] max-w-[calc(100vw-2rem)] gap-0 p-4"
        aria-labelledby="tauri-updater-title"
        aria-describedby="tauri-updater-description"
        side="top"
        align="start"
        sideOffset={0}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onEscapeKeyDown={onClose}
      >
        <div className="min-w-0">
          <PopoverHeader className="flex-row items-center gap-3 pe-8">
            <Info aria-hidden="true" className="size-4 shrink-0 text-text-secondary" />
            <PopoverTitle
              id="tauri-updater-title"
              className={typographyVariant({
                variant: 'p2SemiBold',
                className: 'min-w-0 flex-1',
              })}
            >
              {formatMessage({
                defaultMessage: 'New version available',
                id: 'unK1eMWz3T',
                description: 'Title of the desktop update prompt',
              })}
            </PopoverTitle>
          </PopoverHeader>
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            className="absolute end-3 top-3"
            aria-label={formatMessage({
              defaultMessage: 'Dismiss update',
              id: 'iiVwP5OxZ9',
              description: 'Accessible label for dismissing the desktop update prompt',
            })}
            onClick={onClose}
          >
            <X />
          </Button>
          <PopoverDescription
            id="tauri-updater-description"
            className="mt-1 text-pretty ps-7"
          >
            {formatMessage({
              defaultMessage:
                'An improved version of Heidi is available. Please restart now to upgrade.',
              id: 'T9C3YugcdB',
              description: 'Description of the desktop update prompt',
            })}
          </PopoverDescription>
          <div className="mt-3 flex justify-end">
            <Button
              variant="secondary"
              size="sm"
              disabled={isInstalling}
              onClick={onInstall}
            >
              {isInstalling && <Loader className="text-current" />}
              {formatMessage({
                defaultMessage: 'Restart and upgrade',
                id: 'IYFrPbCzEA',
                description: 'Button to install the desktop update and restart Heidi',
              })}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
